/**
 * Single source of truth for rendering Customer 360 timestamps.
 *
 * Two layers:
 *  - `formatDateTime(d)` — string formatter, used when the consumer needs a
 *    string (e.g. building DetailItem field values for the right-side panel).
 *  - `<DateTimeDisplay value={d} />` — JSX wrapper around `<time>` for the
 *    common case of inline rendering. Forwards `style`/`className` so the
 *    existing inline-style call sites stay terse.
 *
 * Output rules (matching what the agent app needs):
 *  - Date-only inputs (`"2026-04-14"`) render as `"Apr 14, 2026"` — never
 *    fabricate a midnight time.
 *  - Datetime inputs whose time component is exactly `00:00:00` are
 *    treated as date-only too — that's almost always a date stored in a
 *    datetime column upstream.
 *  - Genuine datetimes render as `"Apr 14, 2026, 1:30 PM"`.
 *  - When the source string carries TZ info (`Z` or `±HH:MM`) the user's
 *    local TZ abbreviation is appended (e.g. `" PDT"`) so converted times
 *    are unambiguous. Naive timestamps render without a TZ — there's no
 *    honest answer to "in which zone?" so we don't pretend.
 */
import type { CSSProperties, ReactNode } from 'react';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const MIDNIGHT_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]00:00(:00(\.0+)?)?(Z|[+-]00:?00)?$/;
const TZ_SUFFIX_RE = /Z$|[+-]\d{2}:?\d{2}$/;

const DATE_ONLY_OPTS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

/** "Apr 14, 2026" — always date-only, even when time is present. */
export function formatDate(d: string): string {
  // `new Date("YYYY-MM-DD")` parses as UTC midnight, then renders as the
  // previous day in any timezone west of UTC. Construct local midnight for
  // date-only inputs so the calendar date survives.
  let parsed: Date;
  if (DATE_ONLY_RE.test(d)) {
    const [y, m, day] = d.split('-').map(Number);
    parsed = new Date(y, m - 1, day);
  } else {
    parsed = new Date(d);
  }
  return parsed.toLocaleDateString('en-US', DATE_ONLY_OPTS);
}

/** Smart formatter — date-only when no time component, date + time
 *  (+ TZ abbreviation when source carries TZ info) otherwise. */
export function formatDateTime(d: string | null | undefined): string {
  if (!d) return '';
  const trimmed = d.trim();
  if (!trimmed) return '';
  if (DATE_ONLY_RE.test(trimmed)) return formatDate(trimmed);
  if (MIDNIGHT_DATETIME_RE.test(trimmed)) {
    return formatDate(trimmed.split(/[T ]/)[0]);
  }
  const hasTz = TZ_SUFFIX_RE.test(trimmed);
  return new Date(trimmed).toLocaleString('en-US', {
    ...DATE_ONLY_OPTS,
    hour: 'numeric',
    minute: '2-digit',
    ...(hasTz ? { timeZoneName: 'short' as const } : {}),
  });
}

interface DateTimeDisplayProps {
  value: string | null | undefined;
  style?: CSSProperties;
  className?: string;
  /** Rendered when `value` is empty/missing. Defaults to nothing. */
  fallback?: ReactNode;
  /** Override the title (hover) text. Defaults to the raw timestamp,
   *  which is handy for debugging field provenance. */
  title?: string;
}

export function DateTimeDisplay({
  value,
  style,
  className,
  fallback = null,
  title,
}: DateTimeDisplayProps) {
  const formatted = formatDateTime(value);
  if (!formatted) return <>{fallback}</>;
  return (
    <time
      dateTime={value ?? undefined}
      title={title ?? value ?? undefined}
      style={style}
      className={className}
    >
      {formatted}
    </time>
  );
}
